import { mapState } from 'vuex'
import { cloneDeep, get, isArray, isBoolean, isNumber, isObject, isString } from 'lodash'
import moment from 'moment'
import api from '../../api/form'
import { isArrayComponent, isObjectComponent } from '../../utils/index'

const DEFAULT_NUMBER_MAX_VALUE = 999999999999998

const mixin = {
  computed: {
    ...mapState({
      config: state => state.config,
      formJson: state => state.formJson,
      formDataId: state => state.formDataId.data,
      errorMessages: state => state.formJson.errorMessages,
      components: state => state.formJson.components,
      clientKey: state => state.config.clientKey
    })
  },
  data () {
    return {
      apiDebounceTimer: null
    }
  },
  methods: {
    debounceApiTrigger () {
      clearTimeout(this.apiDebounceTimer)

      this.apiDebounceTimer = setTimeout(() => {
        this.blurHandler()
      }, 500)
    },
    hasFilledValue (value) {
      if (value === null || value === undefined || value === '') return false
      if (Array.isArray(value) || typeof value === 'string') return value.length > 0
      return true
    },
    hasValidationValue (value) {
      return value !== undefined && value !== null && value !== ''
    },
    getNumberMax () {
      const max = get(this.component, 'validate.max')

      if (this.hasValidationValue(max)) {
        const parsedMax = Number(max)
        return Number.isNaN(parsedMax) ? false : parsedMax
      }

      return this.component.type === 'number' ? DEFAULT_NUMBER_MAX_VALUE : false
    },
    convertStringToDate (value) {
      const dateFormatList = [
        'DDMMyyyy',
        'DDMMYYYY',
        'YYYY-MM-DD',
        'yyyyMMDD'
      ]
      for (let i = 0; i < dateFormatList.length; i++) {
        const d = moment(value, dateFormatList[i])
        if (d.isValid() && d.format(dateFormatList[i]) === value) {
          return d
        }
      }

      return false
    },
    isRequired () {
      // obrigatoriedade
      let required = false

      // Verifica se o componente possui a propriedade disabled
      if (!this.component.hasOwnProperty('validate')) {
        return required
      }
      if (!this.component.validate.hasOwnProperty('required')) {
        return required
      }

      // Atualiza a variavel com o valor atual do required
      required = this.component.validate.required

      // Verifica se não é obrigatório
      if (!required) {
        return required
      }

      // Obrigatoriedade condicional
      // Verifica se o componente possui as propriedades necessárias
      if (!this.component.validate.hasOwnProperty('requiredConditional')) {
        return required
      }
      if (!this.component.validate.requiredConditional.length) {
        return required
      }

      let requiredConditional = this.component.validate.requiredConditional
      let variables = this.config.variables
      let json = this.config.json
      let formData = this.config.formData
      let components = this.formJson.components

      const methodTypeWhen = {
        component: (rule) => typeWhenComponent(rule),
        variable: (rule) => typeWhenVariable(rule)
      }

      const methodTypeCondition = {
        custom: (rule) => checkTypeConditionCustom(rule),
        initial: (rule) => checkTypeConditionInitial(rule),
        component: (rule) => checkTypeConditionComponent(rule),
        variable: (rule) => checkTypeConditionVariable(rule),
        json: (rule) => checkTypeConditionJson(rule),
        today: (rule) => checkTypeConditionToday(rule),
        daysBeforeNow: (rule) => checkTypeConditionDaysBeforeNow(rule),
        daysAfterNow: (rule) => checkTypeConditionDaysAfterNow(rule),
        monthsBeforeNow: (rule) => checkTypeConditionMonthsBeforeNow(rule),
        monthsAfterNow: (rule) => checkTypeConditionMonthsAfterNow(rule),
        yearsBeforeNow: (rule) => checkTypeConditionYearsBeforeNow(rule),
        yearsAfterNow: (rule) => checkTypeConditionYearsAfterNow(rule)
      }

      const methodTypeValue = {
        string: (value) => validTypeValueString(value),
        boolean: (value) => validTypeValueBoolean(value),
        number: (value) => validTypeValueNumber(value),
        date: (value) => validTypeValueDate(value),
        array: (value) => validTypeValueArray(value)
      }

      const typeWhenComponent = (rule) => {
        const component = this.$findComponentByKey(rule.when, components)

        if (component) {
          rule.when = cloneDeep(component)
          return methodTypeCondition[rule.typeCondition](rule)
        }
      }
      const typeWhenVariable = (rule) => {
        // Verifica se a ariavel existe nas variaveis injetadas
        if (variables && variables.hasOwnProperty(rule.when)) {
          // Atualiza p valor do when com o valor da variavel existente nas variaveis injetadas
          rule.when = cloneDeep(variables[rule.when])

          return methodTypeCondition[rule.typeCondition](rule)
        }
      }

      const checkTypeConditionCustom = (rule) => {
        let x, y
        let type = rule.typeValue
        // atualiza os valores

        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (x) {
              if (isObjectComponent(rule.when)) {
                x = x.value
              }
              if (isArrayComponent(rule.when)) {
                if (x && x.length > 0) {
                  x = x.map(item => item.value)
                  type = 'array'
                } else {
                  return false
                }
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            }

            return this.$comparingDate(x, rule.operator, y)
          }
          case 'array': {
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }
            let result = false
            for (let item of x) {
              if (!methodTypeValue[rule.typeValue](item)) {
                continue
              }
              result = this.$comparing(item, rule.operator, y)
              if (result) {
                return result
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }
            return this.$comparing(x, rule.operator, y)
          }
        }
      }
      const checkTypeConditionInitial = (rule) => {
        let x, y
        let type = rule.typeValue

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            y = rule.when.data.initialValue
            break
          default:
            return false
        }

        if ((x && x.length > 0) && (y && y.length > 0)) {
          if (isObjectComponent(rule.when)) {
            x = x.value
            y = y.value
          }
          if (isArrayComponent(rule.when)) {
            x = x.map(item => item.value)
            y = y.map(item => item.value)
            type = 'array'
          }
        } else {
          return false
        }

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }

            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            let result = false
            for (let itemX of x) {
              if (!methodTypeValue[rule.typeValue](itemX)) {
                continue
              }
              for (let itemY of y) {
                if (!methodTypeValue[rule.typeValue](itemY)) {
                  continue
                }
                result = this.$comparing(itemX, rule.operator, itemY)
                if (result) {
                  return result
                }
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }
            return this.$comparing(x, rule.operator, y)
          }
        }
      }
      const checkTypeConditionComponent = (rule) => {
        let x, y
        let type = rule.typeValue
        const component = this.$findComponentByKey(rule.value, components)

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (x && isObjectComponent(rule.when)) {
              x = x.value
            }
            if (isArrayComponent(rule.when)) {
              if (x && x.length > 0) {
                x = x.map(item => item.value)
                type = 'array'
              } else {
                return false
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }
        if (!component) {
          return false
        }
        y = component.data.currentValue
        let componentIsArray = false
        if (y && isObjectComponent(component)) {
          y = y.value
        }
        if (isArrayComponent(component)) {
          if (y && y.length > 0) {
            y = y.map(item => item.value)
            componentIsArray = true
          } else {
            return false
          }
        }

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (!methodTypeValue[rule.typeValue](y)) {
              return false
            }
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            }

            return this.$comparingDate(x, rule.operator, y)
          }
          case 'array': {
            if (componentIsArray) {
              let result = false
              for (let itemX of x) {
                if (!methodTypeValue[rule.typeValue](itemX)) {
                  continue
                }
                for (let itemY of y) {
                  if (!methodTypeValue[rule.typeValue](itemY)) {
                    continue
                  }
                  result = this.$comparing(itemX, rule.operator, itemY)
                  if (result) {
                    return result
                  }
                }
              }
              return result
            } else {
              if (!methodTypeValue[rule.typeValue](y)) {
                return false
              }
              let result = false
              for (let item of x) {
                if (!methodTypeValue[rule.typeValue](item)) {
                  continue
                }
                result = this.$comparing(item, rule.operator, y)
                if (result) {
                  return result
                }
              }
              return result
            }
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) {
              return false
            }
            if (componentIsArray) {
              let result = false
              for (let item of y) {
                if (!methodTypeValue[rule.typeValue](item)) {
                  continue
                }
                result = this.$comparing(x, rule.operator, item)
                if (result) {
                  return result
                }
              }
            } else {
              return this.$comparing(x, rule.operator, y)
            }
            break
          }
        }
      }
      const checkTypeConditionVariable = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (x && isObjectComponent(rule.when)) {
              x = x.value
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value
        if (!variables.hasOwnProperty(y)) {
          return false
        }
        y = variables[y]

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }
        if (!methodTypeValue[rule.typeValue](y)) {
          return false
        }

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionJson = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (x && isObjectComponent(rule.when)) {
              x = x.value
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        let array = y.split('.')
        for (var i = 0; i < array.length; i++) {
          switch (array[i]) {
            case 'json':
              y = json
              break
            case 'formData':
              y = formData
              break
            default:
              y = get(y, array[i])
          }
        }

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }
        if (!methodTypeValue[rule.typeValue](y)) {
          return false
        }

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionToday = (rule) => {
        let x, y
        let today = moment().startOf('day').format('DDMMyyyy')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = today

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }
        if (!methodTypeValue[rule.typeValue](y)) {
          return false
        }

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionDaysBeforeNow = (rule) => {
        let x, y
        let today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }

        // converte em data
        if (x) {
          x = this.convertStringToDate(x)
        }
        if (!x) {
          return false
        }

        const days = Number(y)
        y = today.subtract(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionDaysAfterNow = (rule) => {
        let x, y
        let today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }

        // converte em data
        if (x) {
          x = this.convertStringToDate(x)
        }
        if (!x) {
          return false
        }

        const days = Number(y)
        y = today.add(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsBeforeNow = (rule) => {
        let x, y
        let today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }

        // converte em data
        if (x) {
          x = this.convertStringToDate(x)
        }
        if (!x) {
          return false
        }

        y = today.subtract(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsAfterNow = (rule) => {
        let x, y
        let today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) {
          return false
        }

        // converte em data
        if (x) {
          x = this.convertStringToDate(x)
        }
        if (!x) {
          return false
        }

        y = today.add(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.subtract(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.add(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }

      // Metodos de validação de tipo de valor
      const validTypeValueString = (value) => {
        return isString(value)
      }
      const validTypeValueBoolean = (value) => {
        return isBoolean(value)
      }
      const validTypeValueNumber = (value) => {
        if (value === null) return true

        return isNumber(value)
      }
      const validTypeValueDate = (value) => {
        if (!value) return true
        return moment(value, 'DDMMyyyy', true).isValid()
      }
      const validTypeValueArray = (value) => {
        return Array.isArray(value)
      }

      // Valida as regras da obrigatoriedade condicional
      const isValidRequiredConditionalRules = requiredConditional.reduce((accumulatorRules, currentValueRules, indexRules, arrayRules) => {
        // Percorre as regras [E]
        let isValidCurrentValueRules = currentValueRules.reduce((accumulatorRule, currentValueRule, indexRule, arrayRule) => {
          // Verifica se existe metodo para o type when
          if (methodTypeWhen.hasOwnProperty(currentValueRule.typeWhen)) {
            return accumulatorRule + methodTypeWhen[currentValueRule.typeWhen](cloneDeep(currentValueRule))
          }

          return accumulatorRule
        }, 0)

        return accumulatorRules + (isValidCurrentValueRules === currentValueRules.length ? 1 : 0)
      }, 0)

      // Retorna true/false
      return isValidRequiredConditionalRules > 0
    },
    isConditional () {
      let valid = false

      // Verifica se o componente possui as propriedades necessárias
      if (!this.component.validate) return valid
      if (!this.component.validate.hasOwnProperty('conditional')) return valid
      if (!this.component.validate.conditional.length) return valid

      let conditional = this.component.validate.conditional
      let variables = this.config.variables
      let json = this.config.json
      let formData = this.config.formData
      let components = this.formJson.components

      const methodTypeWhen = {
        component: (rule) => typeWhenComponent(rule),
        variable: (rule) => typeWhenVariable(rule)
      }

      const methodTypeCondition = {
        custom: (rule) => checkTypeConditionCustom(rule),
        initial: (rule) => checkTypeConditionInitial(rule),
        component: (rule) => checkTypeConditionComponent(rule),
        variable: (rule) => checkTypeConditionVariable(rule),
        json: (rule) => checkTypeConditionJson(rule),
        today: (rule) => checkTypeConditionToday(rule),
        daysBeforeNow: (rule) => checkTypeConditionDaysBeforeNow(rule),
        daysAfterNow: (rule) => checkTypeConditionDaysAfterNow(rule),
        monthsBeforeNow: (rule) => checkTypeConditionMonthsBeforeNow(rule),
        monthsAfterNow: (rule) => checkTypeConditionMonthsAfterNow(rule),
        yearsBeforeNow: (rule) => checkTypeConditionYearsBeforeNow(rule),
        yearsAfterNow: (rule) => checkTypeConditionYearsAfterNow(rule)
      }

      const methodTypeValue = {
        string: (value) => validTypeValueString(value),
        boolean: (value) => validTypeValueBoolean(value),
        number: (value) => validTypeValueNumber(value),
        date: (value) => validTypeValueDate(value),
        array: (value) => validTypeValueArray(value)
      }

      const typeWhenComponent = (rule) => {
        const component = this.$findComponentByKey(rule.when, components)

        if (component) {
          rule.when = cloneDeep(component)

          return methodTypeCondition[rule.typeCondition](rule)
        }
      }
      const typeWhenVariable = (rule) => {
        // Verifica se a ariavel existe nas variaveis injetadas
        if (variables.hasOwnProperty(rule.when)) {
          // Atualiza p valor do when com o valor da variavel existente nas variaveis injetadas
          rule.when = cloneDeep(variables[rule.when])

          return methodTypeCondition[rule.typeCondition](rule)
        }
      }

      const checkTypeConditionCustom = (rule) => {
        let x, y
        let type = rule.typeValue
        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              if (x) {
                x = x.value
              }
              if (!x) {
                x = ''
              }
            }
            if (isArrayComponent(rule.when)) {
              if (x && x.length > 0) {
                x = x.map(item => item.value)
                type = 'array'
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            if (!methodTypeValue[rule.typeValue](y)) return false
            let result = false
            for (let item of x) {
              if (!methodTypeValue[rule.typeValue](item)) continue
              result = this.$comparing(item, rule.operator, y)
              if (result) {
                return result
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            return this.$comparing(x, rule.operator, y)
          }
        }
      }
      const checkTypeConditionInitial = (rule) => {
        let x, y
        let type = rule.typeValue
        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            y = rule.when.data.initialValue
            break
          default:
            return false
        }
        if (isObjectComponent(rule.when)) {
          if (x) {
            x = x.value
            y = y.value
          }
          if (!x) {
            x = ''
          }
        }
        if (isArrayComponent(rule.when)) {
          if ((x && x.length > 0) && (y && y.length > 0)) {
            x = x.map(item => item.value)
            y = y.map(item => item.value)
            type = 'array'
          }
        } else {
          return false
        }

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false

            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            let result = false
            for (let itemX of x) {
              if (!methodTypeValue[rule.typeValue](itemX)) continue
              for (let itemY of y) {
                if (!methodTypeValue[rule.typeValue](itemY)) continue
                result = this.$comparing(itemX, rule.operator, itemY)
                if (result) {
                  return result
                }
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            return this.$comparing(x, rule.operator, y)
          }
        }

        // validação para o type value date
        /**
         if (rule.typeValue === 'date') {
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
                return comparing(x, rule.operator, y)
            } else {
                return comparingDate(x, rule.operator, y)
            }
        }
         else {
            return comparing(x, rule.operator, y)
        }
         */
      }
      const checkTypeConditionComponent = (rule) => {
        let x, y
        let type = rule.typeValue
        const component = this.$findComponentByKey(rule.value, components)

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              x = x.value
            }
            if (isArrayComponent(rule.when)) {
              if (x && x.length > 0) {
                x = x.map(item => item.value)
                type = 'array'
              } else {
                return false
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }
        if (!component) return false
        y = component.data.currentValue
        let componentIsArray = false
        if (isObjectComponent(component)) {
          y = y.value
        }
        if (isArrayComponent(component)) {
          if (y && y.length > 0) {
            y = y.map(item => item.value)
            componentIsArray = true
          } else {
            return false
          }
        }
        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            if (componentIsArray) {
              let result = false
              for (let itemX of x) {
                if (!methodTypeValue[rule.typeValue](itemX)) continue
                for (let itemY of y) {
                  if (!methodTypeValue[rule.typeValue](itemY)) continue
                  result = this.$comparing(itemX, rule.operator, itemY)
                  if (result) {
                    return result
                  }
                }
              }
              return result
            } else {
              if (!methodTypeValue[rule.typeValue](y)) return false
              let result = false
              for (let item of x) {
                if (!methodTypeValue[rule.typeValue](item)) continue
                result = this.$comparing(item, rule.operator, y)
                if (result) {
                  return result
                }
              }
              return result
            }
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (componentIsArray) {
              let result = false
              for (let item of y) {
                if (!methodTypeValue[rule.typeValue](item)) continue
                result = this.$comparing(x, rule.operator, item)
                if (result) {
                  return result
                }
              }
            } else {
              return this.$comparing(x, rule.operator, y)
            }
            break
          }
        }
      }
      const checkTypeConditionVariable = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              x = x.value
            }
            if (isArrayComponent(rule.when)) {
              if (x) {
                x = x.map(item => item.value)
                x = x.toString()
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        if (!variables.hasOwnProperty(y)) return false
        y = variables[y]
        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionJson = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              x = x.value
            }
            if (isArrayComponent(rule.when)) {
              x = x.map(item => item.value)
              x = x.toString()
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        let array = y.split('.')
        for (var i = 0; i < array.length; i++) {
          switch (array[i]) {
            case 'json':
              y = json
              break
            case 'formData':
              y = formData
              break
            default:
              y = get(y, array[i])
          }
        }
        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionToday = (rule) => {
        let x, y
        let today = moment().startOf('day').format('DDMMyyyy')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = today

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionDaysBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        const days = Number(y)
        y = today.subtract(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionDaysAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        const days = Number(y)
        y = today.add(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.subtract(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.add(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.subtract(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.add(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }

      // Metodos de validação de tipo de valor
      const validTypeValueString = (value) => {
        return isString(value)
      }
      const validTypeValueBoolean = (value) => {
        return isBoolean(value)
      }
      const validTypeValueNumber = (value) => {
        if (value === null) return true

        return isNumber(value)
      }
      const validTypeValueDate = (value) => {
        if (!value) return true
        return moment(value, 'DDMMyyyy', true).isValid()
      }
      const validTypeValueArray = (value) => {
        return Array.isArray(value)
      }

      // Valida as regras da condicional
      const isValidConditionalRules = conditional.reduce((accumulatorRules, currentValueRules, indexRules, arrayRules) => {
        // Percorre as regras [E]
        let isValidCurrentValueRules = currentValueRules.reduce((accumulatorRule, currentValueRule, indexRule, arrayRule) => {
          // Verifica se existe metodo para o type when
          if (methodTypeWhen.hasOwnProperty(currentValueRule.typeWhen)) {
            return accumulatorRule + methodTypeWhen[currentValueRule.typeWhen](cloneDeep(currentValueRule))
          }

          return accumulatorRule
        }, 0)

        return accumulatorRules + (isValidCurrentValueRules === currentValueRules.length ? 1 : 0)
      }, 0)

      // Retorna true/false
      return isValidConditionalRules > 0
    },
    getAllComponents () {
      const allComps = []
      allComps.push(...this.components)
      this.components.forEach(x => x.components && allComps.push(...x.components))

      return allComps
    },
    getClass () {
      return {
        'has-focus': this.isFocus,
        'has-value': this.isValue,
        'has-disabled': this.disabledHandler(),
        'has-error': this.errors.has(this.component.key),
        'has-alert': this.errors.firstByRule(`${this.component.key}`, 'alerts', null),
        'no-description': (!this.errors.has(this.component.key) && !this.component.description)
      }
    },
    setRules () {
      let rules = {
        required: this.isRequired(),
        min: (this.component.validate.min) ? this.component.validate.min : false,
        max: this.getNumberMax(),
        minLength: (this.component.validate.minLength) ? Number.parseInt(this.component.validate.minLength, 10) : false,
        maxLength: (this.component.validate.maxLength) ? Number.parseInt(this.component.validate.maxLength, 10) : false,
        fileMin: (this.component.validate.fileMin) ? this.component.validate.fileMin : false,
        fileMax: (this.component.validate.fileMax) ? this.component.validate.fileMax : false,
        date_format: (this.component.type === 'datepicker') ? 'ddMMyyyy' : false,
        regex: (this.component.validate.typeOfValidation === 'regex') ? this.component.validate.pattern : false,
        conditional: (this.component.validate.hasOwnProperty('conditional') && this.component.validate.conditional.length) ? [this.isConditional()] : false
      }

      // Validação customizada: Pre-definida
      // Validação Pré-definida: cpf, cnpj, cpf ou cnpj, celular, telefone, celular ou telefone, email
      if (this.component.validate.typeOfValidation === 'default') {
        if (this.component.validate.predefined) {
          rules[this.component.validate.predefined] = true
        }
      }
      if (this.component.type === 'textarea') {
        this.$validator.fields.items.forEach(i => {
          if (i.name === 'area-de-texto') {
            delete i.rules.max
            delete i.rules.min
          }
        })
      }
      return rules
    },
    setValue () {
      let type = this.component.type

      if (type === 'number' && this.component.validate && !this.hasValidationValue(this.component.validate.max)) {
        this.$set(this.component.validate, 'max', DEFAULT_NUMBER_MAX_VALUE)
      }

      this.$set(this.component.data, 'currentValue', this.component.data.defaultValue)

      // Default
      if (this.component.data.currentValue) {
        this.isValue = true
      }

      // Bind
      if (this.formDataId) {
        if (this.formDataId[this.component.key]) {
          // if (type === 'select') {
          //   this.component.data.currentValue = this.formDataId[this.component.key].value
          // } else {
          this.component.data.currentValue = this.formDataId[this.component.key]
          // }
          this.isValue = true
        }
      }

      // De-Para
      if (this.config.json) {
        if (!this.component.keyCustom) {
          this.component.keyCustom = 'json.' + this.component.key
        }

        let y
        let keyCustom = cloneDeep(this.component.keyCustom)
        let array = keyCustom.split('.')
        for (var i = 0; i < array.length; i++) {
          switch (array[i]) {
            case 'json':
              y = this.config.json
              break
            case 'formDataId':
              y = this.formDataId
              break
            default:
              let tmpY = get(y, array[i])
              if (!tmpY) {
                y = get(y, this.component.key)
                i = array.length
              } else {
                y = tmpY
              }
          }
        }

        if (['text', 'textarea', 'password', 'datepicker', 'help-block'].includes(type)) {
          if (isString(y)) {
            this.component.data.currentValue = y
            this.isValue = true
          }
        } else if (['number'].includes(type)) {
          if (isNumber(y)) {
            this.component.data.currentValue = y
            this.isValue = true
          }
        } else if (['checkbox', 'radio'].includes(type)) {
          if (isArray(y)) {
            this.component.data.currentValue = y
            this.isValue = true
          }
        } else if (['checkbox-single'].includes(type)) {
          if (isBoolean(y)) {
            this.component.data.currentValue = y
            this.isValue = true
          }
        } else if (type.includes('select')) {
          if (isArray(y)) {
            this.component.data.currentValue = y
            this.isValue = true
          }
        }
      }

      if (this.component.type === 'datepicker') {
        const date = this.convertStringToDate(this.component.data.currentValue)
        if (date) {
          this.component.data.currentValue = date.format('DDMMyyyy')
        }
      }
      if (this.component.data.currentValue[0].value === '') {
        this.component.data.currentValue = null
      }
      this.$set(this.component.data, 'initialValue', this.component.data.currentValue)
      this.$set(this.component.data, 'currentValue', this.component.data.currentValue)
    },
    focusHandler (state = true) {
      this.isFocus = state
      this.$validator.errors.remove(this.component.key)
    },
    enterHandler () {
      const milliseconds = new Date().getTime()
      const config = this.$store.getters.config
      const payload = {
        origin: 'formBuilderWebRender',
        client: config.identifier,
        type: 'message',
        message: 'submit-event',
        data: null,
        timestamp: milliseconds
      }
      window.parent.postMessage(payload, '*')
    },
    async blurHandler (state) {
      this.clientKeyStorage = await localStorage.getItem('clientKey')
      this.focusHandler(false)
      if (this.$validator.fields.items.length) {
        this.$nextTick(() => {
          this.$validator.validate(this.component.key, this.component.data.currentValue)
        })
      }

      // eventos
      let objHeader = {}
      let objBody = {}
      let filtratBody = []
      let resultData = null
      let errorObject = null
      let resp = {}

      if (this.component.eventsData && this.component.eventsData.activate === true) {
        // monta o header
        this.component.eventsData.headers.map(el => {
          objHeader[el.header] = el.value
        })
        // monta o body
        this.component.eventsData.requestBody.forEach(filtros => {
          if (filtros.typeCondition === 'component') {
            this.getAllComponents().forEach(comp => {
              if (comp.key === filtros.value) {
                filtratBody.push({ 'tag': filtros.tag, 'value': comp.data.currentValue })
              }
            })
          }
          if (filtros.typeCondition === 'custom') {
            filtratBody.push({ 'tag': filtros.tag, 'value': filtros.value })
          }
        })
        filtratBody.map(el => {
          objBody[el.tag] = el.value
        })
        this.$store.commit('setLoadEvents', true)

        try { // envia a requisição para a url inserida
          resp = await api.getResoruceApi(
            this.component.eventsData.urlEvents,
            objHeader,
            objBody,
            this.clientKey ? this.clientKey : this.clientKeyStorage,
            this.component.data.securityGroup ? this.component.data.securityGroup : this.component.eventsData.securityGroup
          )
          if (resp.status !== 200) {
            errorObject = {
              'field': `${this.component.key}`,
              'msg': 'Erro de conexão com o serviço.'
            }
          } else {
            resultData = resp.data.data
          }

          // verifica se algum campo foi inserido errado
          if (this.component.eventsData.validateComponent === true) {
            if (!resultData) {
              errorObject = {
                'field': `${this.component.key}`,
                'msg': 'Falha ao validar campo.'
              }
            }
          }

          // se a validação não estiver ativada e a api não responder
          if (this.component.eventsData.validateComponent === false) {
            if (!resultData) {
              errorObject = {
                'field': `${this.component.key}`,
                'msg': 'Erro de conexão com o serviço',
                'rule': 'alerts'
              }
              setTimeout(() => {
                this.$validator.errors.remove(this.component.key)
              }, 10000)
            }
          }

          // triggers
          this.component.eventsData.trigger.forEach(keyTrigges => {
            this.getAllComponents().forEach(async Keycomponents => {
              if (keyTrigges.component === Keycomponents.key) {
                let propriedade = keyTrigges.propriedadeLabel.split('.') //  dados.selects.select => ['dados', 'selects', 'select']
                let resultTrigger = null
                for (let i = 0; i < propriedade.length; i++) {
                  if (!resultData) {
                    resultTrigger = null
                    break
                  }
                  if (resultTrigger) {
                    resultTrigger = resultTrigger[propriedade[i]]
                  } else {
                    resultTrigger = resultData[propriedade[i]]
                  }
                }
                // tratar retorno diferente para o tipo especifico do componente
                if (Keycomponents.type === 'number') {
                  // 0 é um valor válido (ex.: saldo zerado); strings numéricas são convertidas para number
                  let numberValue = resultTrigger
                  if (isString(numberValue) && numberValue.trim() !== '') {
                    numberValue = numberValue.replace(/[^\d,.-]/g, '')
                    // formato pt-BR (1.234,56): remove separador de milhar e troca a vírgula decimal
                    if (numberValue.includes(',')) {
                      numberValue = numberValue.replace(/\./g, '').replace(',', '.')
                    }
                    numberValue = numberValue === '' ? NaN : Number(numberValue)
                  }
                  // sem valor válido (ex.: falha na API) volta ao valor inicial, como no app
                  if (isNumber(numberValue) && !Number.isNaN(numberValue)) {
                    Keycomponents.data.currentValue = numberValue
                  } else {
                    Keycomponents.data.currentValue = isNumber(Keycomponents.data.initialValue) ? Keycomponents.data.initialValue : null
                  }
                  return
                }

                if (Keycomponents.type === 'text' || Keycomponents.type === 'textarea') {
                  Keycomponents.data.currentValue = resultTrigger || ''
                  return
                }

                if (Keycomponents.type === 'checkbox' || Keycomponents.type === 'checkbox-single') {
                  let valueNew = false
                  if (resultTrigger === 'true') valueNew = true
                  Keycomponents.data.currentValue = valueNew
                  return
                }

                if (Keycomponents.type === 'select') {
                  if (Array.isArray(resultTrigger)) {
                    const values = []
                    if (Keycomponents.isMultiselect) {
                      resultTrigger.forEach((item, i) => {
                        Keycomponents.some((el) => {
                          if (el.label[i] === item.label[i]) {
                            values.push(el)
                            return true
                          }
                          return false
                        })
                      })
                    } else {
                      Keycomponents.data.currentValue = resultTrigger
                      return
                    }
                    Keycomponents.data.currentValue = values
                  }
                } else if (Keycomponents.type === 'select-api') {
                  if (Keycomponents.isMultiselect) {
                    Keycomponents.data.currentValue = resultTrigger
                  } else {
                    Keycomponents.data.currentValue = [resultTrigger[0]]
                  }
                } else if (Keycomponents.type === 'select-resource') {
                  if (Array.isArray(resultTrigger)) {
                    const values = []
                    if (Keycomponents.isMultiselect) {
                      for (let i = 0; i < resultTrigger.length; i++) {
                        const item = resultTrigger[i]
                        try {
                          let label = item.label[0]
                          const resp = await api.getResource(
                            this.clientKey ? this.clientKey : this.clientKeyStorage,
                            Keycomponents.data.resourceId,
                            label,
                            { grouper: [] },
                            { subGrouper: [] }
                          )

                          if (resp.data.data.length > 0) {
                            values.push([{ label: [resp.data.data[0].description], value: resp.data.data[0].code }])
                          }
                        } catch (error) {
                        }
                      }
                    } else {
                      Keycomponents.data.currentValue = ''
                    }
                  }
                }
                if (Keycomponents.type === 'select-api') {
                  Keycomponents.data.currentValue = [{ label: [resultTrigger[0].label[0]], value: resultTrigger.value }]
                }
                if (Keycomponents.type === 'select-resource') {
                  let label = resultTrigger[0].label[0]
                  api.getResource(
                    this.clientKey,
                    Keycomponents.data.resourceId,
                    label,
                    { grouper: [] },
                    { subGrouper: [] }
                  ).then(resp => {
                    if (resp.data.data.length === 0) {
                      Keycomponents.data.currentValue = null
                    }
                    // Keycomponents.data.currentValue = values
                  })
                } else if (resultTrigger) {
                  Keycomponents.data.currentValue = resultTrigger
                }
              }
            })
          })

          setTimeout(() => {
            if (this.component.eventsData.onBlurComponent) {
              document.getElementById(`${this.component.eventsData.onBlurComponent}`).focus()
            }
          }, 1000)
        } catch (e) {
          errorObject = {
            'field': `${this.component.key}`,
            'msg': 'Erro de conexão com o serviço.'
          }
          if (e.response) {
            resp = e.response
          }
        }

        this.$store.commit('setLoadEvents', false)
      }
      let b = await this.$validator.validate(this.component.key, this.component.data.currentValue)
      if (!errorObject && !b) {
        errorObject = {
          'field': `${this.component.key}`,
          'msg': this.$validator.errors.first(this.component.key)
        }
      }

      if (errorObject) {
        if (resp && resp.data && resp.data.mensagem) {
          if (this.component.eventsData.validateComponent) {
            errorObject.msg = resp.data.mensagem
          } else {
            errorObject.msg = 'Não foi possível obter os dados referentes a esse campo.'
            errorObject.rule = 'alerts'
          }
        } else if (this.component.validate.errorMessage) {
          errorObject.msg = this.component.validate.errorMessage
          delete errorObject.rule
        }

        this.$validator.errors.remove(this.component.key)
        this.$validator.errors.add(errorObject)
      }
    },
    disabledHandler () {
      // desabilitar
      let disabled = false

      // Verifica se o componente possui a propriedade disabled
      if (!this.component.hasOwnProperty('rule')) return disabled
      if (!this.component.rule.hasOwnProperty('disabled')) return disabled

      // Atualiza a variavel com o valor atual do disabled
      disabled = this.component.rule.disabled

      // Verifica se não é obrigatório
      if (!disabled) return disabled

      // Obrigatoriedade condicional
      // Verifica se o componente possui as propriedades necessárias
      if (!this.component.rule.hasOwnProperty('disabledConditional')) return disabled
      if (!this.component.rule.disabledConditional.length) return disabled

      let disabledConditional = this.component.rule['disabledConditional']
      let variables = this.config.variables
      let json = this.config.json
      let formData = this.config.formData
      let components = this.formJson.components

      const methodTypeWhen = {
        component: (rule) => typeWhenComponent(rule),
        variable: (rule) => typeWhenVariable(rule)
      }

      const methodTypeCondition = {
        custom: (rule) => checkTypeConditionCustom(rule),
        initial: (rule) => checkTypeConditionInitial(rule),
        component: (rule) => checkTypeConditionComponent(rule),
        variable: (rule) => checkTypeConditionVariable(rule),
        json: (rule) => checkTypeConditionJson(rule),
        today: (rule) => checkTypeConditionToday(rule),
        daysBeforeNow: (rule) => checkTypeConditionDaysBeforeNow(rule),
        daysAfterNow: (rule) => checkTypeConditionDaysAfterNow(rule),
        monthsBeforeNow: (rule) => checkTypeConditionMonthsBeforeNow(rule),
        monthsAfterNow: (rule) => checkTypeConditionMonthsAfterNow(rule),
        yearsBeforeNow: (rule) => checkTypeConditionYearsBeforeNow(rule),
        yearsAfterNow: (rule) => checkTypeConditionYearsAfterNow(rule)
      }

      const methodTypeValue = {
        string: (value) => validTypeValueString(value),
        boolean: (value) => validTypeValueBoolean(value),
        number: (value) => validTypeValueNumber(value),
        date: (value) => validTypeValueDate(value),
        array: (value) => validTypeValueArray(value)
      }

      const typeWhenComponent = (rule) => {
        const component = this.$findComponentByKey(rule.when, components)

        if (component) {
          rule.when = cloneDeep(component)

          return methodTypeCondition[rule.typeCondition](rule)
        }
      }
      const typeWhenVariable = (rule) => {
        // Verifica se a ariavel existe nas variaveis injetadas
        if (variables.hasOwnProperty(rule.when)) {
          // Atualiza p valor do when com o valor da variavel existente nas variaveis injetadas
          rule.when = cloneDeep(variables[rule.when])

          return methodTypeCondition[rule.typeCondition](rule)
        }
      }

      const checkTypeConditionCustom = (rule) => {
        let x, y
        let type = rule.typeValue
        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (x) {
              if (isObjectComponent(rule.when)) {
                x = x.value
              }
              if (isArrayComponent(rule.when)) {
                if (x && x.length > 0) {
                  x = x.map(item => item.value)
                  type = 'array'
                } else {
                  return false
                }
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            if (!methodTypeValue[rule.typeValue](y)) return false
            let result = false
            for (let item of x) {
              if (!methodTypeValue[rule.typeValue](item)) continue
              result = this.$comparing(item, rule.operator, y)
              if (result) {
                return result
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            return this.$comparing(x, rule.operator, y)
          }
        }
      }
      const checkTypeConditionInitial = (rule) => {
        let x, y
        let type = rule.typeValue

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            y = rule.when.data.initialValue
            break
          default:
            return false
        }

        if ((x && x.length > 0) && (y && y.length > 0)) {
          if (isObjectComponent(rule.when)) {
            x = x.value
            y = y.value
          }
          if (isArrayComponent(rule.when)) {
            x = x.map(item => item.value)
            y = y.map(item => item.value)
            type = 'array'
          }
        } else {
          return false
        }

        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false

            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            let result = false
            for (let itemX of x) {
              if (!methodTypeValue[rule.typeValue](itemX)) continue
              for (let itemY of y) {
                if (!methodTypeValue[rule.typeValue](itemY)) continue
                result = this.$comparing(itemX, rule.operator, itemY)
                if (result) {
                  return result
                }
              }
            }
            return result
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            return this.$comparing(x, rule.operator, y)
          }
        }

        // validação para o type value date
        /**
         if (rule.typeValue === 'date') {
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
                return comparing(x, rule.operator, y)
            } else {
                return comparingDate(x, rule.operator, y)
            }
        }
         else {
            return comparing(x, rule.operator, y)
        }
         */
      }
      const checkTypeConditionComponent = (rule) => {
        let x, y
        let type = rule.typeValue
        const component = this.$findComponentByKey(rule.value, components)

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              x = x.value
            }
            if (isArrayComponent(rule.when)) {
              if (x && x.length > 0) {
                x = x.map(item => item.value)
                type = 'array'
              } else {
                return false
              }
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        if (!component) return false
        y = component.data.currentValue
        let componentIsArray = false
        if (isObjectComponent(component)) {
          y = y.value
        }
        if (isArrayComponent(component)) {
          if (y && y.length > 0) {
            y = y.map(item => item.value)
            componentIsArray = true
          } else {
            return false
          }
        }
        // validação para o type value date
        switch (type) {
          case 'date': {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (!methodTypeValue[rule.typeValue](y)) return false
            // converte em data
            if (x) x = this.convertStringToDate(x)
            if (y) y = this.convertStringToDate(y)

            if (!x || !y) {
              return this.$comparing(x, rule.operator, y)
            } else {
              return this.$comparingDate(x, rule.operator, y)
            }
          }
          case 'array': {
            if (componentIsArray) {
              let result = false
              for (let itemX of x) {
                if (!methodTypeValue[rule.typeValue](itemX)) continue
                for (let itemY of y) {
                  if (!methodTypeValue[rule.typeValue](itemY)) continue
                  result = this.$comparing(itemX, rule.operator, itemY)
                  if (result) {
                    return result
                  }
                }
              }
              return result
            } else {
              if (!methodTypeValue[rule.typeValue](y)) return false
              let result = false
              for (let item of x) {
                if (!methodTypeValue[rule.typeValue](item)) continue
                result = this.$comparing(item, rule.operator, y)
                if (result) {
                  return result
                }
              }
              return result
            }
          }
          default: {
            // valida o tipo do valor
            if (!methodTypeValue[rule.typeValue](x)) return false
            if (componentIsArray) {
              let result = false
              for (let item of y) {
                if (!methodTypeValue[rule.typeValue](item)) continue
                result = this.$comparing(x, rule.operator, item)
                if (result) {
                  return result
                }
              }
            } else {
              return this.$comparing(x, rule.operator, y)
            }
            break
          }
        }
      }
      const checkTypeConditionVariable = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObject(rule.when.type)) {
              x = x.value
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }
        y = rule.value

        if (!variables.hasOwnProperty(y)) return false
        y = variables[y]

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionJson = (rule) => {
        let x, y

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            if (isObjectComponent(rule.when)) {
              x = x.value
            }
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        let array = y.split('.')
        for (var i = 0; i < array.length; i++) {
          switch (array[i]) {
            case 'json':
              y = json
              break
            case 'formData':
              y = formData
              break
            default:
              y = get(y, array[i])
          }
        }

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionToday = (rule) => {
        let x, y
        let today = moment().startOf('day').format('DDMMyyyy')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = today

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false
        if (!methodTypeValue[rule.typeValue](y)) return false

        // validação para o type value date
        if (rule.typeValue === 'date') {
          // converte em data
          if (x) x = this.convertStringToDate(x)
          if (y) y = this.convertStringToDate(y)

          if (!x || !y) {
            return this.$comparing(x, rule.operator, y)
          } else {
            return this.$comparingDate(x, rule.operator, y)
          }
        } else {
          return this.$comparing(x, rule.operator, y)
        }
      }
      const checkTypeConditionDaysBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        const days = Number(y)
        y = today.subtract(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionDaysAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        const days = Number(y)
        y = today.add(days, 'days')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.subtract(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionMonthsAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.add(y, 'months')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsBeforeNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.subtract(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }
      const checkTypeConditionYearsAfterNow = (rule) => {
        let x, y
        const today = moment().startOf('day')

        // atualiza os valores
        switch (rule.typeWhen) {
          case 'component':
            x = rule.when.data.currentValue
            break
          case 'variable':
            x = rule.when
            break
          default:
            return false
        }

        y = rule.value

        // valida o tipo do valor
        if (!methodTypeValue[rule.typeValue](x)) return false

        // converte em data
        if (x) x = this.convertStringToDate(x)
        if (!x) return false

        y = today.add(y, 'years')

        return this.$comparingDate(x, rule.operator, y)
      }

      // Metodos de validação de tipo de valor
      const validTypeValueString = (value) => {
        return isString(value)
      }
      const validTypeValueBoolean = (value) => {
        return isBoolean(value)
      }
      const validTypeValueNumber = (value) => {
        if (value === null) return true

        return isNumber(value)
      }
      const validTypeValueDate = (value) => {
        if (!value) return true
        return moment(value, 'DDMMyyyy', true).isValid()
      }

      const validTypeValueArray = (value) => {
        return Array.isArray(value)
      }

      // Valida as regras da obrigatoriedade condicional
      const isValidDisabledConditionalRules = disabledConditional.reduce((accumulatorRules, currentValueRules, indexRules, arrayRules) => {
        // Percorre as regras [E]
        let isValidCurrentValueRules = currentValueRules.reduce((accumulatorRule, currentValueRule, indexRule, arrayRule) => {
          // Verifica se existe metodo para o type when
          if (methodTypeWhen.hasOwnProperty(currentValueRule.typeWhen)) {
            return accumulatorRule + methodTypeWhen[currentValueRule.typeWhen](cloneDeep(currentValueRule))
          }

          return accumulatorRule
        }, 0)

        return accumulatorRules + (isValidCurrentValueRules === currentValueRules.length ? 1 : 0)
      }, 0)

      // Atualiza com o valor atual
      disabled = isValidDisabledConditionalRules > 0

      // Retorna true/false
      return disabled
    }
  },
  watch: {
    'component.data.currentValue': function (after, before) {
      this.isValue = this.hasFilledValue(after) ? 1 : 0
      this.disabledHandler()
      if (
        this.isValue &&
        this.component.eventsData &&
        this.component.eventsData.activate === true
      ) {
        this.debounceApiTrigger()
      }
    }
  }
}

export default mixin
